import React, { useState } from 'react';
import styled from 'styled-components';

import PlusIconWithRoundBg from '../../svgs/PlusIconWithRoundBg';
import { applyCSSOverrides } from '../../utility/styled';
import GlassHoverOverlay from './GlassHoverOverlay';

import type { CSSProperties, ReactNode } from 'react';

interface AddIconWrapperProps {
  children: ReactNode;
  onClick: () => void | Promise<void>;
  disabled?: boolean;
  buttonSize?: number;
  customStyles?: CSSProperties;
  customStylesWhenHovered?: CSSProperties;
  overlayStyles?: CSSProperties;
  /**
   * Show the picker-card hover treatment (dim + glass button with this label)
   * instead of the plus icon — matches the Graphics > Overlays cards.
   */
  hoverLabel?: string;
  onMouseEnter?: (e: React.MouseEvent) => void;
  onMouseMove?: (e: React.MouseEvent) => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
}

const AddIconWrapper: React.FC<AddIconWrapperProps> = ({
  children,
  onClick,
  disabled = false,
  buttonSize = 24,
  customStyles,
  customStylesWhenHovered,
  overlayStyles,
  hoverLabel,
  onMouseEnter,
  onMouseMove,
  onMouseLeave,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      await onClick();
    }
  };

  return (
    <Container
      onMouseEnter={e => {
        setIsHovered(true);
        onMouseEnter?.(e);
      }}
      onMouseMove={e => {
        onMouseMove?.(e);
      }}
      onMouseLeave={e => {
        setIsHovered(false);
        onMouseLeave?.(e);
      }}
      onClick={handleClick}
      disabled={disabled}
      customStyles={customStyles}
      customStylesWhenHovered={customStylesWhenHovered}
      isHovered={isHovered}
    >
      {children}
      {isHovered &&
        !disabled &&
        (hoverLabel ? (
          <GlassHoverOverlay label={hoverLabel} />
        ) : (
          <Overlay customStyles={overlayStyles}>
            <PlusIconWithRoundBg
              width={buttonSize.toString()}
              height={buttonSize.toString()}
              strokeColor="var(--primary)"
              backgroundColor="var(--background)"
            />
          </Overlay>
        ))}
    </Container>
  );
};

export default AddIconWrapper;

const Container = styled.div<{
  disabled?: boolean;
  customStyles?: CSSProperties;
  customStylesWhenHovered?: CSSProperties;
  isHovered?: boolean;
}>`
  position: relative;
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.6 : 1)};
  &,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  ${props => applyCSSOverrides(props.customStyles)}

  ${props =>
    props.isHovered && applyCSSOverrides(props.customStylesWhenHovered)}
`;

const Overlay = styled.div<{ customStyles?: CSSProperties }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  ${props => applyCSSOverrides(props.customStyles)}
`;
